/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { alpha, Box, Grid, Typography } from '@mui/material';

import { DonutChart, Money, PieChart } from '../index';

import { AllocationChartProps } from './props';

export const AllocationChart: React.FC<AllocationChartProps> = (props) => {
  const {
    data,
    totalKeyName,
    labelKey,
    valueKey,
    isDonut = true,
    isPercentage = false,
    total,
    colorList = [
      alpha('#67CA24', 0.8),
      alpha('#B24Bf1', 0.8),
      alpha('#3D5AFE', 0.8),
      alpha('#F59E0B', 0.8),
      alpha('#FF4E4E', 0.8),
      // '#81c784', // green
      // '#e57373', // red
      // '#fff176', // yellow
      // 'rgb(106, 215, 198)',
      // 'rgb(82, 93, 188)',
      // 'rgb(78, 152, 248)',
    ],
    isMoney = false,
    ...rest
  } = props;

  // ======================= VARIABLES
  const totalAmount =
    total ??
    data?.find((item) => item?.[labelKey] === totalKeyName)?.[valueKey];
  const labelList = totalKeyName
    ? data
        ?.filter(
          (item) =>
            item?.[labelKey] !== totalKeyName && item?.[labelKey] !== null
        )
        ?.map((item) => item?.[labelKey])
    : data?.map((item) => item?.[labelKey]);
  let dataList: any[];
  if (isPercentage) {
    dataList = totalKeyName
      ? data
          ?.filter(
            (item) =>
              item?.[labelKey] !== totalKeyName && item?.[labelKey] !== null
          )
          ?.map((item) =>
            (((item?.[valueKey] ?? 0) / (totalAmount ?? 0)) * 100).toFixed(2)
          )
      : data?.map((item) =>
          (((item?.[valueKey] ?? 0) / (totalAmount ?? 0)) * 100).toFixed(2)
        );
  } else {
    dataList = totalKeyName
      ? data
          ?.filter(
            (item) =>
              item?.[labelKey] !== totalKeyName && item?.[labelKey] !== null
          )
          ?.map((item) => item?.[valueKey])
      : data?.map((item) => item?.[valueKey]);
  }
  const allocationData = {
    labels: labelList,
    series: dataList,
    total: totalAmount,
  };

  // ======================= RENDERER FUNCTIONS
  const renderChart = () => {
    if (isDonut) {
      return <DonutChart data={allocationData} colors={colorList} />;
    }
    return (
      <PieChart data={allocationData} colors={colorList} isMoney={isMoney} />
    );
  };

  // ======================= VIEW
  return (
    <Grid container>
      <Grid
        item
        xs={12}
        lg={6}
        xl={5}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          px: 'auto',
          maxHeight: { xs: '220px', lg: '200px' },
        }}
      >
        {renderChart()}
      </Grid>
      <Grid
        item
        xs={12}
        lg={6}
        xl={7}
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: { xs: 5, lg: 0 },
        }}
      >
        <Grid container>
          {allocationData?.labels?.map((label, index) => (
            <Grid
              display={'flex'}
              key={index}
              xs={12}
              md={6}
              lg={12}
              py={1}
              justifyContent={'center'}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: { xs: '90%', md: '70%', lg: '100%' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    width: { xs: '45%', md: 'unset', lg: '50%' },
                  }}
                >
                  <span
                    style={{
                      borderRadius: '100%',
                      width: '12px',
                      minWidth: '12px',
                      minHeight: '12px',
                      height: '12px',
                      backgroundColor: colorList[index],
                    }}
                  />
                  <Typography
                    key={label}
                    variant="body2"
                    color={'secondary'}
                    fontWeight={500}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={label}
                  >
                    {label}
                  </Typography>
                </Box>
                {isMoney ? (
                  <Money
                    amount={allocationData?.series?.[index]}
                    currency={'RM'}
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      textAlign: 'right',
                    }}
                  />
                ) : (
                  <Typography variant={'body2'} fontWeight={500}>
                    {isPercentage
                      ? `${allocationData?.series?.[index]}%`
                      : `${allocationData?.series?.[index]}`}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export * from './props';
